import { type RefObject, useEffect, useState } from 'react';
import { useCurrentEditor } from '@tiptap/react';

import { isRichTextToolbarEngaged } from '@/lib/editor-portal-targets';
import { getDocument } from '@/lib/utils';
import { RichTextToolbarContent } from '../rich-text-toolbar-content';
import { FloatingBlockToolbarPortal } from './floating-block-toolbar-portal';

export interface FloatingRichTextToolbarProps {
  anchorRef: RefObject<HTMLElement | null>;
  headingsOnly?: boolean;
  visible?: boolean;
}

/**
 * Positions the rich text formatting toolbar beside an anchor element.
 * Must render inside a TipTap EditorProvider (e.g. within BaseEditableContent).
 */
export function FloatingRichTextToolbar({
  anchorRef,
  headingsOnly = false,
  visible = true,
}: FloatingRichTextToolbarProps) {
  const { editor } = useCurrentEditor();
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarSessionActive, setToolbarSessionActive] = useState(false);

  useEffect(() => {
    if (!editor || !visible) {
      setShowToolbar(false);
      return;
    }

    const syncVisibility = () => {
      setShowToolbar(isRichTextToolbarEngaged(editor, { sessionActive: toolbarSessionActive }));
    };

    // focusin fires before TipTap updates isFocused; defer so engagement sees the new focus.
    const scheduleSync = () => {
      queueMicrotask(syncVisibility);
    };

    const doc = getDocument();
    doc?.addEventListener('focusin', scheduleSync);
    doc?.addEventListener('focusout', scheduleSync);
    syncVisibility();

    return () => {
      doc?.removeEventListener('focusin', scheduleSync);
      doc?.removeEventListener('focusout', scheduleSync);
    };
  }, [editor, visible, toolbarSessionActive]);

  if (!visible || !showToolbar) {
    return null;
  }

  return (
    <FloatingBlockToolbarPortal anchorRef={anchorRef} placement="above">
      <div className="pointer-events-auto flex items-center">
        <RichTextToolbarContent
          headingsOnly={headingsOnly}
          onToolbarSessionActiveChange={setToolbarSessionActive}
        />
      </div>
    </FloatingBlockToolbarPortal>
  );
}
